import React from 'react';
import { Result, Skeleton, Flex } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { PageHeader } from '@ant-design/pro-layout';
import { DefaultPageLayout } from 'layouts/default-page-layout';
import { useGetPaymentPeriodStatistics } from 'api/statistic/use-get-payment-period-statistics';
import { useGetProfitAggregatedStatistic } from 'api/statistic/use-get-profit-aggregated-statistic';
import { useGetProfitStatisticsByDays } from 'api/statistic/use-get-profit-statistics-by-days';
import { UserRole, withRoleGuard } from 'shared/hocs/with-role-guard';
import { useStatisticProfitFilterStore } from 'shared/stores/use-statistic-profit-filters';
import { LinearChart } from 'components/insights/linear-chart';
import { PaymentPeriodStatistic } from 'components/statistics/payment-period-statistic';
import { ProfitAggregatedStatistic } from 'components/statistics/profit-aggregated-statistic';
import { StatisticProfitHeader } from 'components/statistics/statistic-profit-header';

const ProfitStatisticPageComponent: React.FC = () => {
  const { filters } = useStatisticProfitFilterStore();
  const { data: aggregatedStatisticData, isLoading: isAggregatedStatisticLoading } = useGetProfitAggregatedStatistic({
    filters,
  });
  const { data: statisticByDayData, isLoading: isStatisticByDayLoading } = useGetProfitStatisticsByDays({ filters });
  const { data: originalPaymentPeriod, isLoading: isLoadingOriginalPaymentPeriod } = useGetPaymentPeriodStatistics({
    filters,
    paymentTerm: 'original',
  });
  const { data: scanPaymentPeriod, isLoading: isLoadingScanPaymentPeriod } = useGetPaymentPeriodStatistics({
    filters,
    paymentTerm: 'scan',
  });

  const isLoading =
    isAggregatedStatisticLoading ||
    isStatisticByDayLoading ||
    isLoadingOriginalPaymentPeriod ||
    isLoadingScanPaymentPeriod;

  const mainSeries = [
    {
      name: 'Profit by mileage',
      data: statisticByDayData?.series?.totalProfit ?? [],
      unit: '€',
    },
    {
      name: 'Profit by map',
      data: statisticByDayData?.series?.totalProfitByMap ?? [],
      unit: '€',
    },
  ];

  const comparisonSeries = statisticByDayData?.comparisonSeries
    ? [
        {
          name: 'Profit by mileage',
          data: statisticByDayData.comparisonSeries.totalProfit ?? [],
          unit: '€',
        },
        {
          name: 'Profit by map',
          data: statisticByDayData.comparisonSeries.totalProfitByMap ?? [],
          unit: '€',
        },
      ]
    : undefined;

  const header = <PageHeader title={<StatisticProfitHeader />} />;

  return (
    <DefaultPageLayout header={header}>
      {!filters.userId ? (
        <Result
          icon={<UserOutlined />}
          title='Select a manager'
          subTitle='Please select a manager to view their transport load statistics'
          status='info'
        />
      ) : (
        <Skeleton loading={isLoading}>
          <Flex gap={16} style={{ marginBottom: '16px' }} wrap>
            {aggregatedStatisticData && (
              <ProfitAggregatedStatistic
                value={aggregatedStatisticData.value}
                comparisonValue={aggregatedStatisticData?.comparisonValue}
              />
            )}

            <Flex gap={16}>
              {originalPaymentPeriod && (
                <PaymentPeriodStatistic
                  title='Payment term by original'
                  uncompletedInvoicesAmount={originalPaymentPeriod.uncompletedInvoicesAmount}
                  value={originalPaymentPeriod.value}
                  comparisonValue={originalPaymentPeriod.comparisonValue}
                />
              )}

              {scanPaymentPeriod && (
                <PaymentPeriodStatistic
                  title='Payment term by scan'
                  uncompletedInvoicesAmount={scanPaymentPeriod.uncompletedInvoicesAmount}
                  value={scanPaymentPeriod.value}
                  comparisonValue={scanPaymentPeriod.comparisonValue}
                />
              )}
            </Flex>
          </Flex>
          <LinearChart
            title='Profits'
            mainSeries={mainSeries}
            comparisonSeries={comparisonSeries}
            // withOppositeYAxis
          />
        </Skeleton>
      )}
    </DefaultPageLayout>
  );
};

export const ProfitStatisticPage = withRoleGuard(ProfitStatisticPageComponent, [UserRole.admin]);
